import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AssistantMessage } from "../../types/Assistant";
import * as service from "../../services/assistant.service";

interface AssistantState {
  isOpen: boolean;
  /** Company the current thread belongs to — state is per company. */
  companyId: string | null;
  conversationId: string | null;
  messages: AssistantMessage[];
  /** Set while a turn is generating — drives the composer's disabled state. */
  isThinking: boolean;
  /** Name of the tool currently running, for the "searching…" hint. */
  activeTool: string | null;
  error: string | null;
  epoch: number;
}

const initialState: AssistantState = {
  isOpen: false,
  companyId: null,
  conversationId: null,
  messages: [],
  isThinking: false,
  activeTool: null,
  error: null,
  /**
   * Incremented whenever the thread is reset (new chat / company switch). A
   * sendPrompt that resolves against a stale epoch is discarded, so a reply
   * from the abandoned thread cannot resurrect it.
   */
  epoch: 0,
};

export const sendPrompt = createAsyncThunk(
  "assistant/sendPrompt",
  async (args: {
    companyId: string;
    conversationId: string | null;
    content: string;
    epoch: number;
  }) => {
    const result = await service.sendPrompt(
      args.companyId,
      args.conversationId,
      args.content
    );
    return { ...result, epoch: args.epoch };
  }
);

/** Create the thread up front so its socket room can be joined before asking. */
export const startConversation = createAsyncThunk(
  "assistant/startConversation",
  async (args: { companyId: string; epoch: number }) => {
    const conversation = await service.createConversation(args.companyId);
    return { conversation, epoch: args.epoch };
  }
);

export const loadMessages = createAsyncThunk(
  "assistant/loadMessages",
  async (args: { companyId: string; conversationId: string }) =>
    service.fetchMessages(args.companyId, args.conversationId)
);

const STREAMING_ID = "streaming";

const assistantSlice = createSlice({
  name: "assistant",
  initialState,
  reducers: {
    toggleOpen(state) {
      state.isOpen = !state.isOpen;
    },
    close(state) {
      state.isOpen = false;
    },
    newConversation(state) {
      state.conversationId = null;
      state.messages = [];
      state.error = null;
      state.activeTool = null;
      state.isThinking = false;
      state.epoch += 1;
    },
    /**
     * Assistant state is per company: switching companies must not leave the
     * previous company's thread on screen, or let its id be reused in a send.
     */
    setCompany(state, action: PayloadAction<string | null>) {
      if (state.companyId === action.payload) return;
      state.companyId = action.payload;
      state.conversationId = null;
      state.messages = [];
      state.error = null;
      state.activeTool = null;
      state.isThinking = false;
      state.epoch += 1;
    },
    /**
     * A turn starts at the click, not at sendPrompt.pending — creating the
     * thread happens first, and the composer must stay disabled across both.
     */
    beginTurn(state) {
      state.isThinking = true;
      state.error = null;
    },
    /** Optimistic echo of what the employee just typed. */
    addLocalUserMessage(state, action: PayloadAction<string>) {
      state.messages.push({
        id: `temp-${Date.now()}`,
        role: "user",
        content: action.payload,
        status: "done",
        createdAt: Date.now(),
      });
    },
    /** Socket: a chunk of the answer. Accumulates into one streaming bubble. */
    appendDelta(state, action: PayloadAction<{ text: string }>) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.id === STREAMING_ID) {
        last.content += action.payload.text;
      } else {
        state.messages.push({
          id: STREAMING_ID,
          role: "assistant",
          content: action.payload.text,
          status: "streaming",
          createdAt: Date.now(),
        });
      }
    },
    setActiveTool(state, action: PayloadAction<string | null>) {
      state.activeTool = action.payload;
    },
    /** Socket: the persisted message. Replaces whatever the deltas built. */
    receiveCompleted(state, action: PayloadAction<AssistantMessage>) {
      state.messages = state.messages.filter((m) => m.id !== STREAMING_ID);
      if (!state.messages.some((m) => m.id === action.payload.id)) {
        state.messages.push(action.payload);
      }
      state.isThinking = false;
      state.activeTool = null;
    },
    /**
     * `epoch` is optional because socket events carry no epoch — they are
     * already filtered by conversationId. Locally-raised failures DO pass it, so
     * a late failure from an abandoned turn cannot show a false toast or
     * re-enable the composer mid-turn on the new thread.
     */
    receiveFailed(
      state,
      action: PayloadAction<{ errorCode: string; epoch?: number }>
    ) {
      if (
        typeof action.payload.epoch === "number" &&
        action.payload.epoch !== state.epoch
      ) {
        return;
      }
      state.messages = state.messages.filter((m) => m.id !== STREAMING_ID);
      state.isThinking = false;
      state.activeTool = null;
      state.error = action.payload.errorCode;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendPrompt.pending, (state, action) => {
        // A stale turn must not re-arm the spinner: fulfilled/rejected bail on
        // the epoch mismatch, which would leave the composer stuck disabled.
        if (action.meta.arg.epoch !== state.epoch) return;
        state.isThinking = true;
        state.error = null;
      })
      .addCase(sendPrompt.fulfilled, (state, action) => {
        // Thread was reset while this was in flight — drop the result.
        if (action.payload.epoch !== state.epoch) return;
        state.conversationId = action.payload.conversationId;
        // The REST response is authoritative; drop any streaming remnant.
        state.messages = state.messages.filter((m) => m.id !== STREAMING_ID);
        const message = action.payload.assistantMessage;
        if (message && !state.messages.some((m) => m.id === message.id)) {
          state.messages.push(message);
        }
        state.isThinking = false;
        state.activeTool = null;
      })
      .addCase(sendPrompt.rejected, (state, action) => {
        if (action.meta.arg.epoch !== state.epoch) return;
        state.messages = state.messages.filter((m) => m.id !== STREAMING_ID);
        state.isThinking = false;
        state.activeTool = null;
        state.error = action.error.message || "assistant_failed";
      })
      .addCase(loadMessages.fulfilled, (state, action) => {
        state.messages = action.payload;
      })
      .addCase(startConversation.fulfilled, (state, action) => {
        // Thread was reset (new chat / company switch) while create was in
        // flight — adopting the abandoned id would resurrect it and let socket
        // events for it render in the current UI.
        if (action.payload.epoch !== state.epoch) return;
        state.conversationId = action.payload.conversation.id;
      })
      .addCase(startConversation.rejected, (state, action) => {
        if (action.meta.arg.epoch !== state.epoch) return;
        state.isThinking = false;
        state.error = action.error.message || "assistant_failed";
      });
  },
});

export const {
  toggleOpen,
  setCompany,
  beginTurn,
  close,
  newConversation,
  addLocalUserMessage,
  appendDelta,
  setActiveTool,
  receiveCompleted,
  receiveFailed,
} = assistantSlice.actions;

export default assistantSlice.reducer;
